import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, BarChart3, TrendingUp, MousePointerClick, Eye, CheckCircle2, Instagram, Mail, Smartphone } from 'lucide-react';
export function Homepage() {
  const [heroSlug, setHeroSlug] = useState('');
  const [footerSlug, setFooterSlug] = useState('');
  return <div className="min-h-screen bg-brand-offwhite font-sans selection:bg-brand-sage selection:text-brand-forest" data-id="element-829">
      {/* Floating Top Navigation Bar */}
      <nav className="fixed top-0 left-0 right-0 bg-white/90 backdrop-blur-md border-b border-brand-light z-50" data-id="element-830">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between" data-id="element-831">
          <Link to="/" className="flex items-center gap-2" data-id="element-832">
            <img src="https://cdn.magicpatterns.com/uploads/tg3VtfRMGFLgjFkX7oevaV/eaves_favicon_transparent.png" alt="Eaves Pro Logo" className="w-8 h-8" data-id="element-833" />
            <span className="font-serif font-semibold text-xl text-brand-dark" data-id="element-834">
              Eaves Pro
            </span>
          </Link>
          <div className="flex items-center gap-6" data-id="element-835">
            <Link to="/login" className="text-sm font-medium text-brand-dark hover:text-brand-forest transition-colors" data-id="element-836">
              Log in
            </Link>
            <Link to="/login" className="bg-brand-forest text-white text-sm font-medium rounded-full px-5 py-2 hover:bg-brand-forest/90 transition-colors" data-id="element-837">
              Sign up free
            </Link>
          </div>
        </div>
      </nav>

      <main className="pt-16" data-id="element-838">
        {/* Hero Section */}
        <section className="py-24 md:py-32 px-6 max-w-6xl mx-auto overflow-hidden" data-id="element-839">
          <div className="flex flex-col md:flex-row items-center gap-16" data-id="element-840">
            <div className="flex-1 text-center md:text-left z-10" data-id="element-841">
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5
            }} className="inline-block bg-brand-sage text-brand-forest text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full mb-6" data-id="element-842">
                Free for every agent
              </motion.div>
              <motion.h1 initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5,
              delay: 0.1
            }} className="text-5xl md:text-6xl font-serif font-bold text-brand-dark leading-tight" data-id="element-843">
                Your work speaks. <br className="hidden md:block" data-id="element-844" />
                <span className="text-brand-forest" data-id="element-845">Now it has a stage.</span>
              </motion.h1>
              <motion.p initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5,
              delay: 0.2
            }} className="text-xl text-brand-muted mt-6 max-w-lg mx-auto md:mx-0" data-id="element-846">
                Your Eaves Pro profile is already built — from your
                transactions, your reviews, your neighborhood. Claim it in 60
                seconds.
              </motion.p>

              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5,
              delay: 0.3
            }} className="mt-10 flex flex-col sm:flex-row items-center justify-center md:justify-start max-w-md mx-auto md:mx-0 shadow-premium rounded-xl" data-id="element-847">
                <div className="flex w-full sm:w-auto" data-id="element-848">
                  <div className="bg-white border border-brand-light rounded-t-xl sm:rounded-l-xl sm:rounded-tr-none px-4 py-3.5 font-mono text-sm text-brand-muted flex items-center border-b-0 sm:border-b sm:border-r-0" data-id="element-849">
                    eaves.pro/
                  </div>
                  <input type="text" value={heroSlug} onChange={e => setHeroSlug(e.target.value)} placeholder="your-name" className="flex-1 bg-white border-y border-r border-brand-light px-2 py-3.5 font-mono text-sm text-brand-dark focus:outline-none focus:border-brand-forest rounded-tr-xl sm:rounded-none border-b-0 sm:border-b sm:border-r-0" data-id="element-850" />
                </div>
                <Link to={heroSlug ? `/onboarding?slug=${heroSlug}` : '/onboarding'} className="w-full sm:w-auto bg-brand-forest text-white rounded-b-xl sm:rounded-r-xl sm:rounded-bl-none px-6 py-3.5 font-medium hover:bg-brand-forest/90 transition-colors text-center whitespace-nowrap" data-id="element-851">
                  Claim your free profile
                </Link>
              </motion.div>
            </div>

            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.7,
            delay: 0.2
          }} className="flex-1 relative w-full max-w-sm mx-auto" data-id="element-852">
              <motion.div animate={{
              y: [0, -10, 0]
            }} transition={{
              repeat: Infinity,
              duration: 5,
              ease: 'easeInOut'
            }} className="relative z-10 transform rotate-2 shadow-2xl rounded-[2.5rem] bg-white border-[8px] border-brand-dark overflow-hidden aspect-[9/19]" data-id="element-853">
                {/* Mini Profile Mockup */}
                <div className="h-32 bg-gradient-to-b from-brand-sage to-brand-sandstoneBg" data-id="element-854"></div>
                <div className="px-6 pb-6 -mt-12 relative bg-white rounded-t-3xl" data-id="element-855">
                  <div className="w-24 h-24 rounded-full border-4 border-white overflow-hidden bg-brand-light mx-auto shadow-sm" data-id="element-856">
                    <img src="https://images.unsplash.com/photo-1560250097-0b93528c311a?w=400&h=400&fit=crop" alt="Marcus Chen" className="w-full h-full object-cover" data-id="element-857" />
                  </div>
                  <div className="text-center mt-3" data-id="element-858">
                    <h3 className="font-serif font-bold text-xl text-brand-dark" data-id="element-859">
                      Marcus Chen
                    </h3>
                    <p className="text-xs text-brand-muted mt-1" data-id="element-860">
                      Principal Agent · The Chen Group
                    </p>
                  </div>

                  <div className="mt-6 space-y-3" data-id="element-861">
                    <div className="h-12 bg-brand-offwhite rounded-xl border border-brand-light flex items-center px-4" data-id="element-862">
                      <div className="w-6 h-6 rounded-full bg-brand-sage" data-id="element-863"></div>
                      <div className="ml-3 h-3 w-24 bg-brand-light rounded" data-id="element-864"></div>
                    </div>
                    <div className="h-12 bg-brand-offwhite rounded-xl border border-brand-light flex items-center px-4" data-id="element-865">
                      <div className="w-6 h-6 rounded-full bg-brand-sage" data-id="element-866"></div>
                      <div className="ml-3 h-3 w-32 bg-brand-light rounded" data-id="element-867"></div>
                    </div>
                    <div className="h-12 bg-brand-offwhite rounded-xl border border-brand-light flex items-center px-4" data-id="element-868">
                      <div className="w-6 h-6 rounded-full bg-brand-sage" data-id="element-869"></div>
                      <div className="ml-3 h-3 w-20 bg-brand-light rounded" data-id="element-870"></div>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Decorative elements behind phone */}
              <div className="absolute top-1/4 -right-12 w-32 h-32 bg-brand-sage rounded-full blur-3xl opacity-60 -z-10" data-id="element-871"></div>
              <div className="absolute bottom-1/4 -left-12 w-40 h-40 bg-brand-sandstone rounded-full blur-3xl opacity-40 -z-10" data-id="element-872"></div>
            </motion.div>
          </div>
        </section>

        {/* Section 2: 'Your profile, already built' */}
        <section className="bg-white py-24 px-6 overflow-hidden" data-id="element-873">
          <div className="max-w-6xl mx-auto flex flex-col-reverse md:flex-row items-center gap-16" data-id="element-874">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true,
            margin: '-100px'
          }} transition={{
            duration: 0.6
          }} className="flex-1 w-full" data-id="element-875">
              <div className="bg-brand-offwhite p-8 rounded-3xl border border-brand-light shadow-sm relative" data-id="element-876">
                <h4 className="font-serif text-xl text-brand-dark mb-6" data-id="element-877">
                  Your verified record
                </h4>
                <div className="space-y-4" data-id="element-878">
                  <div className="bg-white p-4 rounded-xl shadow-sm border border-brand-light flex items-center justify-between" data-id="element-879">
                    <div className="flex items-center gap-3" data-id="element-880">
                      <div className="w-10 h-10 rounded-full bg-brand-sage flex items-center justify-center text-brand-forest" data-id="element-881">
                        <TrendingUp size={20} data-id="element-882" />
                      </div>
                      <div data-id="element-883">
                        <p className="text-sm font-medium text-brand-dark" data-id="element-884">
                          Sale price vs market
                        </p>
                        <p className="text-xs text-brand-muted" data-id="element-885">
                          Top 6% in 94109
                        </p>
                      </div>
                    </div>
                    <span className="font-mono font-medium text-brand-forest" data-id="element-886">
                      +5.2%
                    </span>
                  </div>
                  <div className="bg-white p-4 rounded-xl shadow-sm border border-brand-light flex items-center justify-between" data-id="element-887">
                    <div className="flex items-center gap-3" data-id="element-888">
                      <div className="w-10 h-10 rounded-full bg-brand-sage flex items-center justify-center text-brand-forest" data-id="element-889">
                        <CheckCircle2 size={20} data-id="element-890" />
                      </div>
                      <div data-id="element-891">
                        <p className="text-sm font-medium text-brand-dark" data-id="element-892">
                          Verified Transactions
                        </p>
                        <p className="text-xs text-brand-muted" data-id="element-893">
                          Last 24 months
                        </p>
                      </div>
                    </div>
                    <span className="font-mono font-medium text-brand-forest" data-id="element-894">
                      347
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true,
            margin: '-100px'
          }} transition={{
            duration: 0.6
          }} className="flex-1" data-id="element-895">
              <span className="text-xs uppercase tracking-widest text-brand-forest font-medium" data-id="element-896">
                Claim your profile
              </span>
              <h2 className="text-3xl md:text-4xl font-serif font-semibold text-brand-dark mt-4 leading-tight" data-id="element-897">
                See what your work looks like when someone's paying attention.
              </h2>
              <p className="text-lg text-brand-muted mt-6 leading-relaxed" data-id="element-898">
                Your Eaves Pro profile is already built — from your
                transactions, your reviews, your neighborhood. Claim it in 60
                seconds, then customize it to make it yours.
              </p>
              <Link to="/onboarding" className="inline-flex items-center gap-2 text-brand-forest font-medium mt-8 hover:text-brand-dark transition-colors group" data-id="element-899">
                Get started for free{' '}
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" data-id="element-900" />
              </Link>
            </motion.div>
          </div>
        </section>

        {/* Section 3: 'One link for everything' */}
        <section className="bg-brand-sage/30 py-24 px-6 overflow-hidden" data-id="element-901">
          <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center gap-16" data-id="element-902">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true,
            margin: '-100px'
          }} transition={{
            duration: 0.6
          }} className="flex-1" data-id="element-903">
              <span className="text-xs uppercase tracking-widest text-brand-forest font-medium" data-id="element-904">
                One Link
              </span>
              <h2 className="text-3xl md:text-4xl font-serif font-semibold text-brand-dark mt-4 leading-tight" data-id="element-905">
                One link for everything sellers need to trust you.
              </h2>
              <p className="text-lg text-brand-muted mt-6 leading-relaxed" data-id="element-906">
                Your performance. Your reviews from every platform. Your
                listings, videos, and intake forms — all in one place, all on a
                link you own.
              </p>
              <Link to="/onboarding" className="inline-flex items-center gap-2 text-brand-forest font-medium mt-8 hover:text-brand-dark transition-colors group" data-id="element-907">
                Get started for free{' '}
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" data-id="element-908" />
              </Link>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true,
            margin: '-100px'
          }} transition={{
            duration: 0.6
          }} className="flex-1 relative w-full" data-id="element-909">
              <div className="relative h-[400px] w-full max-w-md mx-auto" data-id="element-910">
                {/* Stacked Cards Mockup */}
                <motion.div initial={{
                y: 20,
                opacity: 0
              }} whileInView={{
                y: 0,
                opacity: 1
              }} viewport={{
                once: true
              }} transition={{
                delay: 0.2
              }} className="absolute top-10 left-4 right-12 bg-white p-4 rounded-2xl shadow-lg border border-brand-light z-30 flex items-center gap-4" data-id="element-911">
                  <div className="w-12 h-12 rounded-full bg-brand-sage flex items-center justify-center text-brand-forest shrink-0" data-id="element-912">
                    <span className="font-serif font-bold" data-id="element-913">Z</span>
                  </div>
                  <div data-id="element-914">
                    <p className="font-medium text-brand-dark" data-id="element-915">
                      Zillow Reviews
                    </p>
                    <p className="text-sm text-brand-muted" data-id="element-916">
                      4.9 ★ (128 reviews)
                    </p>
                  </div>
                </motion.div>

                <motion.div initial={{
                y: 20,
                opacity: 0
              }} whileInView={{
                y: 0,
                opacity: 1
              }} viewport={{
                once: true
              }} transition={{
                delay: 0.4
              }} className="absolute top-32 left-12 right-4 bg-white p-4 rounded-2xl shadow-lg border border-brand-light z-20 flex items-center gap-4" data-id="element-917">
                  <div className="w-12 h-12 rounded-full bg-brand-sage flex items-center justify-center text-brand-forest shrink-0" data-id="element-918">
                    <span className="font-serif font-bold" data-id="element-919">G</span>
                  </div>
                  <div data-id="element-920">
                    <p className="font-medium text-brand-dark" data-id="element-921">
                      Google Reviews
                    </p>
                    <p className="text-sm text-brand-muted" data-id="element-922">
                      4.8 ★ (84 reviews)
                    </p>
                  </div>
                </motion.div>

                <motion.div initial={{
                y: 20,
                opacity: 0
              }} whileInView={{
                y: 0,
                opacity: 1
              }} viewport={{
                once: true
              }} transition={{
                delay: 0.6
              }} className="absolute top-56 left-8 right-8 bg-white p-4 rounded-2xl shadow-lg border border-brand-light z-10 flex items-center gap-4" data-id="element-923">
                  <div className="w-12 h-12 rounded-full bg-brand-sage flex items-center justify-center text-brand-forest shrink-0" data-id="element-924">
                    <span className="font-serif font-bold" data-id="element-925">S</span>
                  </div>
                  <div data-id="element-926">
                    <p className="font-medium text-brand-dark" data-id="element-927">
                      Selling a home
                    </p>
                    <p className="text-sm text-brand-muted" data-id="element-928">
                      Book a consultation
                    </p>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Section 4: 'Share it everywhere' */}
        <section className="bg-white py-24 px-6 overflow-hidden" data-id="element-929">
          <div className="max-w-6xl mx-auto flex flex-col-reverse md:flex-row items-center gap-16" data-id="element-930">
            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true,
            margin: '-100px'
          }} transition={{
            duration: 0.6
          }} className="flex-1 w-full grid grid-cols-2 gap-4" data-id="element-931">
              <div className="space-y-4 mt-8" data-id="element-932">
                <div className="bg-brand-offwhite p-6 rounded-2xl border border-brand-light shadow-sm flex flex-col items-center text-center" data-id="element-933">
                  <Instagram className="text-brand-muted mb-3" size={24} data-id="element-934" />
                  <div className="w-16 h-16 rounded-full bg-brand-light mb-3" data-id="element-935"></div>
                  <div className="h-2 w-20 bg-brand-light rounded mb-4" data-id="element-936"></div>
                  <div className="text-xs font-mono text-brand-forest bg-brand-sage px-2 py-1 rounded w-full truncate" data-id="element-937">
                    eaves.pro/marcuschen
                  </div>
                </div>
                <div className="bg-brand-dark p-6 rounded-2xl shadow-sm flex flex-col justify-between h-32" data-id="element-938">
                  <div className="flex justify-between items-start" data-id="element-939">
                    <div className="w-8 h-8 rounded bg-white/10" data-id="element-940"></div>
                    <Smartphone className="text-white/50" size={20} data-id="element-941" />
                  </div>
                  <div className="text-xs font-mono text-brand-sage truncate" data-id="element-942">
                    eaves.pro/marcuschen
                  </div>
                </div>
              </div>
              <div className="space-y-4" data-id="element-943">
                <div className="bg-white p-6 rounded-2xl border border-brand-light shadow-premium flex flex-col justify-between h-40" data-id="element-944">
                  <div data-id="element-945">
                    <div className="h-3 w-24 bg-brand-dark rounded mb-2" data-id="element-946"></div>
                    <div className="h-2 w-32 bg-brand-muted rounded" data-id="element-947"></div>
                  </div>
                  <div className="text-xs font-mono text-brand-forest bg-brand-sage/50 px-2 py-1 rounded inline-block w-max" data-id="element-948">
                    eaves.pro/marcuschen
                  </div>
                </div>
                <div className="bg-brand-offwhite p-6 rounded-2xl border border-brand-light shadow-sm flex flex-col items-center justify-center h-48" data-id="element-949">
                  <Mail className="text-brand-muted mb-3" size={24} data-id="element-950" />
                  <div className="h-2 w-full bg-brand-light rounded mb-2" data-id="element-951"></div>
                  <div className="h-2 w-3/4 bg-brand-light rounded mb-6" data-id="element-952"></div>
                  <div className="text-xs font-mono text-brand-forest" data-id="element-953">
                    eaves.pro/marcuschen
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true,
            margin: '-100px'
          }} transition={{
            duration: 0.6
          }} className="flex-1" data-id="element-954">
              <span className="text-xs uppercase tracking-widest text-brand-forest font-medium" data-id="element-955">
                Share everywhere
              </span>
              <h2 className="text-3xl md:text-4xl font-serif font-semibold text-brand-dark mt-4 leading-tight" data-id="element-956">
                One link for social, open houses, and listing presentations.
              </h2>
              <p className="text-lg text-brand-muted mt-6 leading-relaxed" data-id="element-957">
                Share your Eaves Pro profile wherever you show up — Instagram
                bio, business cards, email signatures, QR codes at open houses.
                You own the link. We keep it current automatically.
              </p>
              <div className="mt-6" data-id="element-958">
                <span className="font-mono text-brand-forest bg-brand-sage px-3 py-1.5 rounded-lg inline-block font-medium" data-id="element-959">
                  eaves.pro/your-name
                </span>
              </div>
              <Link to="/onboarding" className="inline-flex items-center gap-2 text-brand-forest font-medium mt-8 hover:text-brand-dark transition-colors group" data-id="element-960">
                Get started for free{' '}
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" data-id="element-961" />
              </Link>
            </motion.div>
          </div>
        </section>

        {/* Section 5: 'See what's working' */}
        <section className="bg-brand-dark text-white py-24 px-6 overflow-hidden" data-id="element-962">
          <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center gap-16" data-id="element-963">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true,
            margin: '-100px'
          }} transition={{
            duration: 0.6
          }} className="flex-1" data-id="element-964">
              <span className="text-xs uppercase tracking-widest text-brand-sage font-medium" data-id="element-965">
                Insights
              </span>
              <h2 className="text-3xl md:text-4xl font-serif font-semibold text-brand-offwhite mt-4 leading-tight" data-id="element-966">
                Know who's looking and what they click.
              </h2>
              <p className="text-lg text-gray-400 mt-6 leading-relaxed" data-id="element-967">
                Track profile views, link clicks, and which content drives the
                most engagement. Simple analytics, no complexity.
              </p>
              <Link to="/onboarding" className="inline-flex items-center gap-2 text-brand-sage font-medium mt-8 hover:text-white transition-colors group" data-id="element-968">
                Get started for free{' '}
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" data-id="element-969" />
              </Link>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-100px'
          }} transition={{
            duration: 0.6
          }} className="flex-1 w-full" data-id="element-970">
              <div className="bg-[#1a2520] p-6 rounded-3xl border border-white/10 shadow-2xl" data-id="element-971">
                <div className="flex items-center justify-between mb-8" data-id="element-972">
                  <h3 className="font-serif text-lg text-white" data-id="element-973">Performance</h3>
                  <div className="text-xs text-gray-400 bg-white/5 px-3 py-1 rounded-full" data-id="element-974">
                    Last 7 days
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-8" data-id="element-975">
                  <div className="bg-white/5 p-4 rounded-xl border border-white/5" data-id="element-976">
                    <div className="flex items-center gap-2 text-gray-400 mb-2" data-id="element-977">
                      <Eye size={16} data-id="element-978" />
                      <span className="text-sm" data-id="element-979">Views</span>
                    </div>
                    <div className="text-3xl font-mono text-white" data-id="element-980">247</div>
                    <div className="text-xs text-brand-sage mt-1" data-id="element-981">
                      ↑ 12% vs last week
                    </div>
                  </div>
                  <div className="bg-white/5 p-4 rounded-xl border border-white/5" data-id="element-982">
                    <div className="flex items-center gap-2 text-gray-400 mb-2" data-id="element-983">
                      <MousePointerClick size={16} data-id="element-984" />
                      <span className="text-sm" data-id="element-985">Clicks</span>
                    </div>
                    <div className="text-3xl font-mono text-white" data-id="element-986">83</div>
                    <div className="text-xs text-brand-sage mt-1" data-id="element-987">
                      ↑ 8% vs last week
                    </div>
                  </div>
                </div>

                {/* Decorative Bar Chart */}
                <div className="h-40 flex items-end justify-between gap-2 px-2" data-id="element-988">
                  {[40, 65, 45, 80, 55, 90, 70].map((height, i) => <div key={i} className="w-full flex gap-1 items-end justify-center group" data-id="element-989">
                      <div className="w-1/2 bg-brand-forest/50 rounded-t-sm group-hover:bg-brand-forest transition-colors" style={{
                    height: `${height}%`
                  }} data-id="element-990"></div>
                      <div className="w-1/2 bg-brand-sage/50 rounded-t-sm group-hover:bg-brand-sage transition-colors" style={{
                    height: `${height * 0.4}%`
                  }} data-id="element-991"></div>
                    </div>)}
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="bg-brand-forest py-24 px-6 text-center" data-id="element-992">
          <div className="max-w-3xl mx-auto" data-id="element-993">
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="text-3xl md:text-4xl font-serif font-semibold text-white" data-id="element-994">
              Your profile is waiting.
            </motion.h2>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="text-xl text-brand-sage mt-4 mb-10" data-id="element-995">
              Claim your free Eaves Pro profile in 60 seconds.
            </motion.p>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2
          }} className="flex flex-col sm:flex-row items-center justify-center max-w-md mx-auto shadow-2xl rounded-xl" data-id="element-996">
              <div className="flex w-full sm:w-auto" data-id="element-997">
                <div className="bg-white border border-white rounded-t-xl sm:rounded-l-xl sm:rounded-tr-none px-4 py-3.5 font-mono text-sm text-brand-muted flex items-center border-b-0 sm:border-b sm:border-r-0" data-id="element-998">
                  eaves.pro/
                </div>
                <input type="text" value={footerSlug} onChange={e => setFooterSlug(e.target.value)} placeholder="your-name" className="flex-1 bg-white border-y border-r border-white px-2 py-3.5 font-mono text-sm text-brand-dark focus:outline-none rounded-tr-xl sm:rounded-none border-b-0 sm:border-b sm:border-r-0" data-id="element-999" />
              </div>
              <Link to={footerSlug ? `/onboarding?slug=${footerSlug}` : '/onboarding'} className="w-full sm:w-auto bg-brand-dark text-white rounded-b-xl sm:rounded-r-xl sm:rounded-bl-none px-6 py-3.5 font-medium hover:bg-black transition-colors text-center whitespace-nowrap" data-id="element-1000">
                Claim your free profile
              </Link>
            </motion.div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-brand-dark text-gray-400 py-12 px-6 border-t border-white/10" data-id="element-1001">
        <div className="max-w-6xl mx-auto flex flex-col items-center" data-id="element-1002">
          <Link to="/" className="flex items-center gap-2 mb-8 opacity-80 hover:opacity-100 transition-opacity" data-id="element-1003">
            <img src="https://cdn.magicpatterns.com/uploads/tg3VtfRMGFLgjFkX7oevaV/eaves_favicon_transparent.png" alt="Eaves Pro Logo" className="w-6 h-6 grayscale" data-id="element-1004" />
            <span className="font-serif font-semibold text-lg text-white" data-id="element-1005">
              Eaves Pro
            </span>
          </Link>

          <div className="flex gap-6 mb-8 text-sm" data-id="element-1006">
            <a href="#" className="hover:text-white transition-colors" data-id="element-1007">
              About
            </a>
            <a href="#" className="hover:text-white transition-colors" data-id="element-1008">
              Privacy
            </a>
            <a href="#" className="hover:text-white transition-colors" data-id="element-1009">
              Terms
            </a>
          </div>

          <div className="text-xs text-center" data-id="element-1010">
            © 2026 Eaves Technologies LLC · witheaves.com
          </div>
        </div>
      </footer>
    </div>;
}